import React from 'react';
import { Box, Grid, Typography, Paper } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Link, useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';

import { PageHeader, MetricCard, SkeletonCards, ErrorState, PermissionState, useDesignTokens } from '../ui';
import { radius } from '../../themes/smartinventoryTheme';
import useGADashboardPage from './useGADashboardPage';

const ATTENTION_ICONS = {
  new_no_orders: StorefrontIcon,
  inactive: ScheduleIcon,
  invoice_issue: ReceiptLongIcon,
};

// `value` is already a whole-number percent from the server — this only
// picks a colour from the sign and appends the symbol, never computes it.
const pctDelta = (value) => {
  if (value == null) return undefined;
  return { direction: value > 0 ? 'up' : value < 0 ? 'down' : 'neutral', text: `${value > 0 ? '+' : ''}${value}%` };
};

const countDelta = (value) => {
  if (value == null) return undefined;
  return { direction: value > 0 ? 'up' : value < 0 ? 'down' : 'neutral', text: `${value > 0 ? '+' : ''}${value}` };
};

const GADashboard = () => {
  const p = useGADashboardPage();
  const { tokens: t } = useDesignTokens();
  const navigate = useNavigate();

  if (p.effectiveStatus === 'loading') {
    return (
      <Box>
        <SkeletonCards rows={1} />
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[0, 1, 2, 3].map((i) => <Grid item xs={6} sm={3} key={i}><SkeletonCards rows={1} /></Grid>)}
        </Grid>
        <SkeletonCards rows={3} />
      </Box>
    );
  }

  if (p.effectiveStatus === 'error') {
    return (
      <ErrorState
        title={p.t("Couldn't load platform data", { defaultValue: "Couldn't load platform data" })}
        description={p.t('The server did not respond. Your data is safe — nothing was changed.', { defaultValue: 'The server did not respond. Your data is safe — nothing was changed.' })}
        request={p.errorInfo?.request}
        retryLabel={p.t('Try again', { defaultValue: 'Try again' })}
        onRetry={() => p.fetchMetrics(false)}
      />
    );
  }

  if (p.effectiveStatus === 'permission') {
    return (
      <PermissionState
        title={p.t("You don't have access to the platform overview", { defaultValue: "You don't have access to the platform overview" })}
        description={p.t('Ask a platform operator for access.', { defaultValue: 'Ask a platform operator for access.' })}
      />
    );
  }

  const d = p.data;
  const companyStatusColorFor = { active: t.success, suspended: t.error, archived: t.muted };
  const activeStatus = d.companiesByStatus.find((row) => row.status === 'active');

  return (
    <Box>
      <PageHeader
        title={p.t('Platform overview', { defaultValue: 'Platform overview' })}
        subtitle={p.t('All companies on SmartInventory, at a glance', { defaultValue: 'All companies on SmartInventory, at a glance' })}
        actions={[
          { label: p.refreshing ? p.t('Refreshing...', { defaultValue: 'Refreshing...' }) : p.t('Refresh'), icon: RefreshIcon, variant: 'outlined', onClick: p.handleRefresh, disabled: p.refreshing },
        ]}
      />

      <Grid container spacing={1.75} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={6} md={3}>
          <MetricCard label={p.t('Companies', { defaultValue: 'Companies' })} value={d.companies.value} delta={countDelta(d.companies.delta)} sparkline={d.companies.sparkline} onClick={() => navigate('/ga/companies')} />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <MetricCard label={p.t('Total users', { defaultValue: 'Total users' })} value={d.totalUsers.value} delta={countDelta(d.totalUsers.delta)} sparkline={d.totalUsers.sparkline} />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <MetricCard label={p.t('Orders · 30 days', { defaultValue: 'Orders · 30 days' })} value={d.ordersLast30Days.value} delta={pctDelta(d.ordersLast30Days.delta)} sparkline={d.ordersLast30Days.sparkline} />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <MetricCard
            label={p.t('Inactive companies', { defaultValue: 'Inactive companies' })}
            value={d.inactiveCompanies.value}
            delta={d.inactiveCompanies.value > 0 ? { direction: 'neutral', text: p.t('no orders in 30d', { defaultValue: 'no orders in 30d' }) } : undefined}
            tone={d.inactiveCompanies.value > 0 ? 'warning' : undefined}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ mb: 1.5 }}>{p.t('New company signups · last 12 weeks', { defaultValue: 'New company signups · last 12 weeks' })}</Typography>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={d.signupsWeekly} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={t.border} />
                <XAxis dataKey="weekStart" tick={{ fontSize: 10.5, fill: t.muted }} tickFormatter={(v) => new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} axisLine={{ stroke: t.border }} tickLine={false} minTickGap={30} />
                <YAxis hide />
                <RechartsTooltip
                  formatter={(value) => value}
                  labelFormatter={(v) => new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                  contentStyle={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: radius.md, fontSize: 12.5 }}
                />
                <Bar dataKey="value" fill={t.accent} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ mb: 1.5 }}>{p.t('Needs attention', { defaultValue: 'Needs attention' })}</Typography>
            {d.attention.length === 0 ? (
              <Typography variant="body2" color="text.secondary">{p.t('Nothing needs your attention right now.', { defaultValue: 'Nothing needs your attention right now.' })}</Typography>
            ) : (
              d.attention.map((item) => {
                const Icon = ATTENTION_ICONS[item.kind] || ScheduleIcon;
                return (
                  <Box key={item.kind} component={Link} to="/ga/companies" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, textDecoration: 'none', color: 'inherit', '&:hover': { bgcolor: 'action.hover' }, borderRadius: `${radius.md}px`, px: 1, mx: -1 }}>
                    <Box sx={{ width: 34, height: 34, borderRadius: `${radius.md}px`, bgcolor: 'warning.light', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon fontSize="small" sx={{ color: 'warning.dark' }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {item.kind === 'new_no_orders' && p.t('{{count}} new companies waiting on orders', { count: item.count, defaultValue: `${item.count} new companies waiting on orders` })}
                        {item.kind === 'inactive' && p.t('{{count}} companies gone inactive', { count: item.count, defaultValue: `${item.count} companies gone inactive` })}
                        {item.kind === 'invoice_issue' && p.t('{{count}} invoice processing issues', { count: item.count, defaultValue: `${item.count} invoice processing issues` })}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{item.detail}</Typography>
                    </Box>
                    <ChevronRightIcon fontSize="small" sx={{ color: 'text.disabled', flexShrink: 0 }} />
                  </Box>
                );
              })
            )}
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="subtitle1">{p.t('Most active companies', { defaultValue: 'Most active companies' })}</Typography>
              <Typography component={Link} to="/ga/companies" variant="body2" fontWeight={600} sx={{ color: 'primary.main', textDecoration: 'none' }}>{p.t('All companies', { defaultValue: 'All companies' })}</Typography>
            </Box>
            {d.topCompanies.length === 0 ? (
              <Typography variant="body2" color="text.secondary">{p.t('No company activity yet.', { defaultValue: 'No company activity yet.' })}</Typography>
            ) : (
              d.topCompanies.map((c, index) => (
                <Box key={c.companyId} component={Link} to={`/ga/companies/${c.companyId}`} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25, borderTop: index > 0 ? '1px solid' : 'none', borderColor: 'divider', textDecoration: 'none', color: 'inherit' }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: `${radius.md}px`, bgcolor: 'action.selected', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary', flexShrink: 0 }} fontSize={11} fontWeight={600}>
                    {c.name ? c.name.trim().slice(0, 2).toUpperCase() : '?'}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>{c.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{p.t('{{count}} users', { count: c.users, defaultValue: `${c.users} users` })}</Typography>
                  </Box>
                  <Box sx={{ width: 110, height: 6, borderRadius: `${radius.sm}px`, bgcolor: 'action.selected', overflow: 'hidden', flexShrink: 0 }}>
                    <Box sx={{ width: `${c.share}%`, height: '100%', bgcolor: 'primary.main' }} />
                  </Box>
                  <Typography variant="body2" fontWeight={700} sx={{ width: 90, textAlign: 'right', flexShrink: 0 }}>{p.t('{{count}} orders', { count: c.orders, defaultValue: `${c.orders} orders` })}</Typography>
                </Box>
              ))
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ mb: 1.5 }}>{p.t('Companies by status', { defaultValue: 'Companies by status' })}</Typography>
            <Box sx={{ display: 'flex', height: 12, borderRadius: `${radius.sm}px`, overflow: 'hidden', mb: 1.5 }}>
              {d.companiesByStatus.map((row) => (
                <Box key={row.status} sx={{ width: `${row.share}%`, bgcolor: companyStatusColorFor[row.status] || 'action.selected' }} />
              ))}
            </Box>
            {d.companiesByStatus.map((row) => (
              <Box key={row.status} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.4 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: `${radius.xs}px`, bgcolor: companyStatusColorFor[row.status] || 'action.selected', flexShrink: 0 }} />
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1, textTransform: 'capitalize' }}>{p.t(row.status)}</Typography>
                <Typography variant="body2" fontWeight={600}>{row.count}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ width: 34, textAlign: 'right' }}>{row.share}%</Typography>
              </Box>
            ))}
            {activeStatus && (
              <Box sx={{ borderTop: '1px solid', borderColor: 'divider', mt: 1.25, pt: 1.25 }}>
                <Typography variant="caption" color="text.secondary">{p.t('Platform health', { defaultValue: 'Platform health' })}</Typography>
                <Typography variant="h6" sx={{ color: 'success.main' }}>{p.t('{{pct}}% active', { pct: activeStatus.share, defaultValue: `${activeStatus.share}% active` })}</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default GADashboard;
